import debounce from 'lodash/debounce';
import React, { useCallback, useMemo } from 'react';
import {
  ControlProps,
  MultiValueGenericProps,
  OptionProps,
  components,
} from 'react-select';
import AsyncSelect from 'react-select/async';
import { useLatest } from 'react-use';

import { MedicalCode, MedicalCodeTag } from '@eluve/blocks';
import { Icon, Stack, cn } from '@eluve/components';
import { ResultOf } from '@eluve/graphql.tada';
import { useTranslation } from '@eluve/i18n';

import {
  MultiValueRemove,
  hiddenIndicatorComponents,
  searchableStyle,
} from './Searchable.styles';
import { searchBillingCodesQuery } from './SearchableBillingCodes.operations';

export type BillingCodeOption = Omit<
  ResultOf<typeof searchBillingCodesQuery>['searchMedicalCodes'][0],
  '__typename'
> & {
  price?: number;
  modifiers?: string[] | null;
  userGroupCodeConfigId: string | null;
};

const Control = (props: ControlProps<BillingCodeOption, true>) => {
  const hasValue = Array.isArray(props.selectProps.value)
    ? props.selectProps.value.length > 0
    : Boolean(props.selectProps.value);

  return (
    <components.Control {...props}>
      <Icon
        name="Search"
        className={cn(
          'shrink-0 transition-all duration-200',
          hasValue ? 'ml-0 w-0 opacity-0' : 'mx-2 opacity-100',
        )}
      />
      {props.children}
    </components.Control>
  );
};

// We'll let the container take care of rendering the whole billing code
// so we don't need an actual label
const MultiValueLabel = (_props: MultiValueGenericProps<BillingCodeOption>) => {
  return null;
};

const MultiValueContainer = (
  props: MultiValueGenericProps<BillingCodeOption>,
) => {
  const { data, selectProps } = props;
  const code = data as BillingCodeOption;

  return (
    <MedicalCode
      code={code.code}
      codeType={code.codeType}
      includeDescription={true}
      description={code.description ?? ''}
      endAdornment={
        selectProps?.isDisabled ? null : (
          <components.MultiValueRemove {...props} />
        )
      }
    />
  );
};

const Option = (props: OptionProps<BillingCodeOption>) => {
  const { data } = props;
  const code = data as BillingCodeOption;

  return (
    <components.Option {...props}>
      <Stack direction="horizontal" align="center" gap={3}>
        <MedicalCodeTag code={code.code} codeType={code.codeType} />
        <div>{code.description}</div>
      </Stack>
    </components.Option>
  );
};

export type CodeSearchFn = (query: string) => Promise<Array<BillingCodeOption>>;

export interface SearchableBillingCodesProps {
  disabled?: boolean;
  onCodeAdded?: (option: BillingCodeOption) => void | Promise<void>;
  onCodeRemoved?: (option: BillingCodeOption) => void | Promise<void>;
  selectedCodes?: BillingCodeOption[];
  shouldRenderValue?: boolean;
  searchCodes: CodeSearchFn;
}

export const SearchableBillingCodes: React.FC<SearchableBillingCodesProps> = ({
  disabled,
  onCodeAdded,
  onCodeRemoved,
  selectedCodes,
  shouldRenderValue,
  searchCodes,
}) => {
  const { t } = useTranslation();
  // Capture this as a ref so that in case the upstream consumer didn't give us
  // a stable/memoized function it will still work with our callback below
  const latestSearchCodes = useLatest(searchCodes);

  const _searchCodes = useCallback(
    (query: string, callback: (codes: BillingCodeOption[]) => void) => {
      latestSearchCodes.current(query).then((codes) => {
        callback(codes);
      });
    },
    [latestSearchCodes],
  );

  const debouncedSearchCodes = useMemo(
    () => debounce(_searchCodes, 250),
    [_searchCodes],
  );

  return (
    <AsyncSelect<BillingCodeOption, true>
      aria-label={t('appointmentTools.billing.searchBillingCodes')}
      unstyled
      isDisabled={disabled}
      controlShouldRenderValue={shouldRenderValue}
      backspaceRemovesValue={!shouldRenderValue}
      placeholder={t('appointmentTools.billing.chooseCodePlaceholder')}
      noOptionsMessage={({ inputValue }) =>
        inputValue
          ? t('appointmentTools.common.noResultsFor', { query: inputValue })
          : t('appointmentTools.billing.enterCodeOrSearchTerm')
      }
      loadingMessage={() => t('appointmentTools.common.searching')}
      isMulti
      value={selectedCodes}
      isClearable={false}
      classNames={{
        container: () => searchableStyle.container(),
        control: ({ isDisabled }) =>
          searchableStyle.control({ disabled: isDisabled }),
        valueContainer: ({ isMulti, hasValue, selectProps }) =>
          searchableStyle.valueContainer({
            hasMultiValue:
              isMulti && hasValue && selectProps.controlShouldRenderValue,
          }),
        menu: () => searchableStyle.menu(),
        menuList: () => searchableStyle.menuList(),
        placeholder: () => searchableStyle.placeholder(),
        multiValue: () => searchableStyle.multiValue(),
        multiValueRemove: () => searchableStyle.multiValueRemove(),
        option: ({ isFocused }) =>
          searchableStyle.option({ optionFocused: isFocused }),
        noOptionsMessage: () => searchableStyle.noOptionsMessage(),
        loadingMessage: () => searchableStyle.loadingMessage(),
        input: () => searchableStyle.input(),
      }}
      onChange={async (input) => {
        const codes = selectedCodes ?? [];
        const removed = codes.filter((f) => !input.includes(f));
        const added = input.filter((f) => !codes.includes(f));

        if (removed.length) {
          const [toRemove] = removed;
          await onCodeRemoved?.(toRemove!);
        }

        if (added.length) {
          const [toAdd] = added;
          await onCodeAdded?.(toAdd!);
        }
      }}
      getOptionValue={(option) => option.id}
      getOptionLabel={(option) => option.code}
      components={{
        ...hiddenIndicatorComponents,
        Control,
        MultiValueContainer,
        MultiValueLabel,
        MultiValueRemove,
        Option,
      }}
      loadOptions={debouncedSearchCodes}
    />
  );
};
